import { Button, InlineNotification, Modal, Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import { TrashCan } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAdmin, useResourceApi } from './adminContext';
import { EmptyState } from './components/EmptyState';
import { BackLink } from './components/BackLink';
import { RecordHistory } from './components/RecordHistory';
import { PageHeader } from './components/PageHeader';
import { asProblem, problemMessage } from './problems';
import { missingRequired } from './fields/required';
import { ResourceForm, ResourcePage } from './ResourcePage';
import { formValues, recordLabel, type FieldDef, type RecordValues, type ResourceDef } from './resources';
import { usesFormPage } from './formPage';
import { detailRoute, NEW_RECORD, newRecordRoute } from './routes';
import { hasPermission } from './session';

/**
 * A record with children: its fields, then a tab per child list (an item's barcodes, categories, options, recipe). Also the
 * create and edit page of a resource whose form is too large for a dialog (`usesFormPage`).
 * Route: /records/:resource/:id?parent=… (children of children need their parent's id); `new` as the id creates one.
 */
export function ResourceDetailPage() {
  const { t } = useTranslation();
  const { resources } = useAdmin();
  const { resource = '', id = '' } = useParams();
  const [search] = useSearchParams();
  const parentId = search.get('parent') ?? undefined;
  const def = resources[resource];
  if (!def)
    return (
      <section className="pos-admin-page">
        <EmptyState heading={t('admin.errors.notFound')} />
      </section>
    );

  return id === NEW_RECORD ? (
    <NewRecord key={`${resource}/new`} def={def} parentId={parentId} />
  ) : (
    <Detail key={`${resource}/${id}`} def={def} id={id} parentId={parentId} />
  );
}

/** Where the record's page goes back to: its list, or its parent's page. */
function useBackTo(def: ResourceDef, parentId?: string) {
  const { resources } = useAdmin();
  const parentDef = def.parent ? resources[def.parent] : undefined;
  return {
    parentDef,
    backTo: def.route ?? (parentDef && parentId ? detailRoute(parentDef.key, parentId) : '/'),
  };
}

/** A part of the record's form: the Details tab, or a field the extension puts on a tab of its own (`fieldTabs`). */
interface FormSection {
  key: string;
  label: string;
  /** The fields it shows; all of them when left out. */
  fields?: readonly string[];
}

function useFormSections(def: ResourceDef): FormSection[] {
  const { t } = useTranslation();
  const { extensions } = useAdmin();
  const own = (extensions[def.key]?.fieldTabs ?? [])
    .map((key) => def.fields.find((f) => f.key === key && !f.hidden))
    .filter((field): field is FieldDef => Boolean(field));
  if (own.length === 0) return [{ key: 'details', label: t('admin.details') }];
  const ownKeys = own.map((f) => f.key);
  return [
    {
      key: 'details',
      label: t('admin.details'),
      fields: def.fields.filter((f) => !ownKeys.includes(f.key)).map((f) => f.key),
    },
    ...own.map((field) => ({ key: field.key, label: t(field.label), fields: [field.key] })),
  ];
}

/** The section whose field a save error names, so the page can open its tab. */
function sectionOf(sections: FormSection[], error: unknown): number | undefined {
  const problem = asProblem(error);
  if (!problem || sections.length < 2) return undefined;
  const names = [problem.field, ...Object.keys(problem.errors ?? {})]
    .filter((name): name is string => typeof name === 'string')
    .map((name) => name.toLowerCase());
  const index = sections.findIndex((section) => section.fields?.some((f) => names.includes(f.toLowerCase())));
  return index >= 0 ? index : undefined;
}

/** One section of the form with the page's buttons below it; every section saves the whole record. */
function SectionForm({
  def,
  section,
  values,
  error,
  submitted,
  onChange,
  onSubmit,
  actions,
  existing,
}: {
  def: ResourceDef;
  section: FormSection;
  values: Record<string, unknown>;
  error: unknown;
  submitted: boolean;
  onChange(values: Record<string, unknown>): void;
  onSubmit(): void;
  actions(formId: string): ReactNode;
  existing?: boolean;
}) {
  const formId = `${useId()}-${section.key}`;
  return (
    <div className="pos-admin-page__details">
      <ResourceForm
        def={def}
        values={values}
        id={formId}
        only={section.fields}
        existing={existing}
        error={error}
        submitted={submitted}
        onChange={onChange}
        onSubmit={onSubmit}
      />
      {actions(formId)}
    </div>
  );
}

/** A new record on its own page; once saved, its page opens, where it can be edited further. */
/** `preset.<field>` values from the address (`newRecordRoute`), for fields the resource has. */
function presetOf(def: ResourceDef, search: URLSearchParams): Record<string, string> {
  const fields = new Set(def.fields.map((f) => f.key));
  return Object.fromEntries(
    [...search.entries()]
      .filter(([key]) => key.startsWith('preset.') && fields.has(key.slice('preset.'.length)))
      .map(([key, value]) => [key.slice('preset.'.length), value]),
  );
}

function NewRecord({ def: base, parentId }: { def: ResourceDef; parentId?: string }) {
  const [search] = useSearchParams();
  const query = search.toString();
  const preset = useMemo(() => presetOf(base, new URLSearchParams(query)), [base, query]);
  // Preset fields are set and not shown, as on a list with `fixed` values.
  const def = useMemo(
    () => ({ ...base, fields: base.fields.map((f) => (f.key in preset ? { ...f, hidden: true } : f)) }),
    [base, preset],
  );
  const { t } = useTranslation();
  const { session, addressSource } = useAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useResourceApi(def, parentId);
  const sections = useFormSections(def);
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...formValues(def), ...preset }));
  const [submitted, setSubmitted] = useState(false);
  const [tab, setTab] = useState(0);
  const { parentDef, backTo } = useBackTo(def, parentId);
  const canWrite = Boolean(def.writePermission) && hasPermission(session, def.writePermission ?? undefined);

  const save = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.create(input),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: [api.path] });
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
      void navigate(detailRoute(def.key, String(created.id), parentId), { replace: true });
    },
    onError: (error) => setTab((current) => sectionOf(sections, error) ?? current),
  });
  const submit = () => {
    setSubmitted(true);
    if (missingRequired(def, values, Boolean(addressSource)).length === 0) save.mutate(values);
  };

  if (!canWrite) {
    return (
      <section className="pos-admin-page">
        <EmptyState heading={t('admin.errors.forbidden')} />
      </section>
    );
  }

  const panel = (section: FormSection) => (
    <SectionForm
      def={def}
      section={section}
      values={values}
      error={save.error}
      submitted={submitted}
      onChange={setValues}
      onSubmit={submit}
      actions={(formId) => (
        <div className="pos-admin-page__form-actions">
          <Button type="submit" form={formId} disabled={save.isPending}>
            {t('admin.create')}
          </Button>
          <Button kind="secondary" onClick={() => void navigate(backTo)}>
            {t('common.cancel')}
          </Button>
        </div>
      )}
    />
  );

  return (
    <section className="pos-admin-page">
      <BackLink to={backTo}>{t(parentDef ? parentDef.title : def.title)}</BackLink>
      <PageHeader title={t('admin.create')} />
      {sections.length === 1 && sections[0] ? (
        panel(sections[0])
      ) : (
        <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
          <TabList aria-label={t(def.title)} contained>
            {sections.map((section) => (
              <Tab key={section.key}>{section.label}</Tab>
            ))}
          </TabList>
          <TabPanels>
            {sections.map((section) => (
              <TabPanel key={section.key}>{panel(section)}</TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </section>
  );
}

function Detail({ def, id, parentId }: { def: ResourceDef; id: string; parentId?: string }) {
  const { t } = useTranslation();
  const { resources, session, extensions, addressSource, history } = useAdmin();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const api = useResourceApi(def, parentId);
  // Edits on top of the loaded record; cleared after a save so the form shows what was saved.
  const [edits, setEdits] = useState<Record<string, unknown>>();
  const [deleting, setDeleting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tab, setTab] = useState(0);
  const sections = useFormSections(def);
  const canWrite = Boolean(def.writePermission) && hasPermission(session, def.writePermission ?? undefined);
  const record = useQuery({ queryKey: [api.path, 'record', id], queryFn: () => api.get(id) });
  const { parentDef, backTo } = useBackTo(def, parentId);
  const values = edits ?? (record.data ? formValues(def, record.data) : undefined);

  const save = useMutation({
    mutationFn: (input: Record<string, unknown>) => api.update(record.data as RecordValues, input),
    onSuccess: (saved) => {
      queryClient.setQueryData([api.path, 'record', id], saved);
      setEdits(undefined);
      void queryClient.invalidateQueries({ queryKey: [api.path] });
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
    onError: (error) => setTab((current) => sectionOf(sections, error) ?? current),
  });
  const remove = useMutation({
    mutationFn: () => api.remove(record.data as RecordValues),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [api.path] });
      void navigate(-1);
    },
  });

  if (record.error) {
    return (
      <section className="pos-admin-page">
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(record.error))}
        />
      </section>
    );
  }
  if (!record.data || !values) return null;

  const extension = extensions[def.key];
  const children = def.children.map((key) => resources[key]).filter((child): child is ResourceDef => Boolean(child));
  // Top-level resources that point here and are listed on this record too (`tabOf`), e.g. a contact's cards.
  const linked = Object.values(resources).flatMap((other) =>
    (other.tabOf ?? []).filter((tab) => tab.parent === def.key).map((tab) => ({ def: other, field: tab.field })),
  );
  const submit = () => {
    setSubmitted(true);
    if (missingRequired(def, values, Boolean(addressSource)).length === 0) save.mutate(values);
  };

  const panel = (section: FormSection) => (
    <SectionForm
      def={def}
      section={section}
      values={values}
      error={save.error}
      submitted={submitted}
      onChange={setEdits}
      onSubmit={submit}
      existing
      actions={(formId) =>
        canWrite && (
          <div className="pos-admin-page__form-actions">
            <Button type="submit" form={formId} disabled={save.isPending}>
              {t('admin.save')}
            </Button>
            {usesFormPage(def, extension) && (
              <Button kind="secondary" onClick={() => void navigate(backTo)}>
                {t('common.cancel')}
              </Button>
            )}
            {save.isSuccess && <span className="pos-admin-page__saved">{t('admin.saved')}</span>}
          </div>
        )
      }
    />
  );
  // Every record's changes, for those who may read the audit log (ADR 0118).
  const showHistory = Boolean(history && hasPermission(session, history.permission));
  // A record with nothing besides its fields shows them without tabs.
  const tabbed =
    sections.length > 1 || children.length > 0 || linked.length > 0 || (extension?.tabs?.length ?? 0) > 0 || showHistory;

  return (
    <section className="pos-admin-page">
      <BackLink to={backTo}>{t(parentDef ? parentDef.title : def.title)}</BackLink>
      <PageHeader
        title={recordLabel(def, record.data)}
        actions={
          <>
            {extension?.actions?.(record.data)}
            {canWrite && (
              <Button kind="danger--ghost" renderIcon={TrashCan} onClick={() => setDeleting(true)}>
                {t('admin.delete')}
              </Button>
            )}
          </>
        }
      />
      {!tabbed && sections[0] && panel(sections[0])}
      {tabbed && (
        <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
          <TabList aria-label={t(def.title)} contained>
            {sections.map((section) => (
              <Tab key={section.key}>{section.label}</Tab>
            ))}
            {children.map((child) => (
              <Tab key={child.key}>{t(child.title)}</Tab>
            ))}
            {linked.map((link) => (
              <Tab key={`${link.def.key}:${link.field}`}>{t(link.def.title)}</Tab>
            ))}
            {extension?.tabs?.map((tab) => (
              <Tab key={tab.label}>{tab.label}</Tab>
            ))}
            {showHistory && <Tab key="history">{t('admin.history.title')}</Tab>}
          </TabList>
          <TabPanels>
            {sections.map((section) => (
              <TabPanel key={section.key}>{panel(section)}</TabPanel>
            ))}
            {children.map((child) => (
              <TabPanel key={child.key}>
                {extension?.childTabs?.[child.key] ? (
                  extension.childTabs[child.key]?.(record.data as RecordValues)
                ) : (
                  <ResourcePage
                    def={child}
                    parentId={id}
                    embedded
                    toolbar={
                      extension?.childToolbars?.[child.key]
                        ? (list) => extension.childToolbars?.[child.key]?.(record.data as RecordValues, list)
                        : undefined
                    }
                    onOpen={
                      child.children.length > 0 || usesFormPage(child, extensions[child.key])
                        ? (r) => void navigate(detailRoute(child.key, r.id, id))
                        : undefined
                    }
                    onCreate={
                      usesFormPage(child, extensions[child.key])
                        ? () => void navigate(newRecordRoute(child.key, id))
                        : undefined
                    }
                  />
                )}
              </TabPanel>
            ))}
            {linked.map((link) => (
              <TabPanel key={`${link.def.key}:${link.field}`}>
                <ResourcePage
                  def={link.def}
                  fixed={{ [link.field]: id }}
                  embedded
                  onOpen={
                    link.def.children.length > 0 || usesFormPage(link.def, extensions[link.def.key])
                      ? (r) => void navigate(detailRoute(link.def.key, r.id))
                      : undefined
                  }
                />
              </TabPanel>
            ))}
            {extension?.tabs?.map((tab) => (
              <TabPanel key={tab.label}>{tab.render(record.data as RecordValues)}</TabPanel>
            ))}
            {showHistory && (
              <TabPanel key="history">
                <RecordHistory targetType={def.key} targetId={String(record.data?.id ?? id)} />
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      )}
      {deleting && (
        <Modal
          open
          danger
          preventCloseOnClickOutside
          modalHeading={t('admin.deleteConfirm')}
          closeButtonLabel={t('carbon.close')}
          primaryButtonText={t('admin.delete')}
          secondaryButtonText={t('common.cancel')}
          onRequestClose={() => setDeleting(false)}
          onRequestSubmit={() => remove.mutate()}
        >
          {remove.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(remove.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
